<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import BaseCard from '@/components/ui/BaseCard.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import IssueFilterBar from '@/components/issues/IssueFilterBar.vue';
import IssueTable from '@/components/issues/IssueTable.vue';
import IssueCardList from '@/components/issues/IssueCardList.vue';
import NewIssueModal from '@/components/issues/NewIssueModal.vue';
import { useIssues, type IssueFilters } from '@/composables/useIssues';
import { useIssuesStore, type IssueListItemDto } from '@/stores/issues';
import type { IssueDetailDto } from '@/composables/useIssue';

const route = useRoute();
const router = useRouter();
const store = useIssuesStore();

// URL-bound filters (per spec §2.2.5 — all state is URL-persistent)
function parseNumberArray(val: string | undefined): number[] | undefined {
  if (!val) return undefined;
  const parts = val.split(',').map(Number).filter((n) => !isNaN(n));
  return parts.length ? parts : undefined;
}

const filters = ref<IssueFilters>({
  tab: (route.query.tab as IssueFilters['tab']) || 'all',
  severity: parseNumberArray(route.query.severity as string),
  category: parseNumberArray(route.query.category as string),
  status: parseNumberArray(route.query.status as string),
  source: parseNumberArray(route.query.source as string),
  search: (route.query.search as string) || undefined,
  aiConfidenceMin: route.query.aiConfidenceMin ? Number(route.query.aiConfidenceMin) : undefined,
  limit: 25,
});

// Sync filters → URL query params
watch(filters, (f) => {
  void router.replace({
    query: {
      ...(f.tab !== 'all' ? { tab: f.tab } : {}),
      ...(f.severity?.length ? { severity: f.severity.join(',') } : {}),
      ...(f.category?.length ? { category: f.category.join(',') } : {}),
      ...(f.status?.length ? { status: f.status.join(',') } : {}),
      ...(f.source?.length ? { source: f.source.join(',') } : {}),
      ...(f.search ? { search: f.search } : {}),
      ...(f.aiConfidenceMin ? { aiConfidenceMin: String(f.aiConfidenceMin) } : {}),
    },
  });
}, { deep: true });

const { listIds, isLoading, isLoadingMore, nextCursor, totalCount, openCount, criticalCount, loadMore, refresh } =
  useIssues(filters);

const items = computed((): IssueListItemDto[] =>
  listIds.value
    .map((id: string) => store.entitiesById.get(id))
    .filter((i): i is NonNullable<ReturnType<typeof store.entitiesById.get>> => i != null) as IssueListItemDto[],
);

// Modal state
const showNewIssueModal = ref(false);

function onIssueCreated(issue: IssueDetailDto) {
  store.prepend(issue);
  showNewIssueModal.value = false;
}

// Bulk action stub
function onBulkAction() {
  alert('Bulk operations coming soon');
}
</script>

<template>
  <div class="flex flex-col gap-4" data-testid="issues-list-view">
    <!-- Header -->
    <div class="flex items-center justify-between gap-3">
      <div>
        <h1 class="text-page text-fg font-semibold">Issues</h1>
        <p class="text-caption text-muted mt-0.5">
          <span class="font-mono tabular-nums">{{ openCount }}</span> open
          <span v-if="criticalCount > 0" class="text-red-600 font-medium ml-2">
            · <span class="font-mono tabular-nums">{{ criticalCount }}</span> critical
          </span>
        </p>
      </div>
      <BaseButton variant="primary" size="md" data-testid="new-issue-button" @click="showNewIssueModal = true">
        + New issue
      </BaseButton>
    </div>

    <BaseCard padding="none">
      <div class="p-4 pb-0">
        <!-- Filter bar (tabs + search + chips) -->
        <IssueFilterBar v-model="filters" />
      </div>

      <!-- Bulk action bar (stub — wired to coming-soon toast) -->
      <div class="flex items-center gap-2 px-4 py-2 border-t border-border bg-canvas-soft">
        <span class="text-caption text-muted">Bulk actions:</span>
        <button
          class="text-caption text-muted hover:text-fg underline underline-offset-2 transition-colors"
          @click="onBulkAction"
        >
          Assign
        </button>
        <button
          class="text-caption text-muted hover:text-fg underline underline-offset-2 transition-colors"
          @click="onBulkAction"
        >
          Change status
        </button>
        <button
          class="text-caption text-muted hover:text-fg underline underline-offset-2 transition-colors"
          @click="onBulkAction"
        >
          Archive
        </button>
      </div>

      <!-- Loading skeleton -->
      <div v-if="isLoading" class="p-4 flex flex-col gap-3">
        <div v-for="i in 5" :key="i" class="h-12 bg-canvas-soft rounded animate-pulse" />
      </div>

      <template v-else>
        <!-- Desktop table (≥769px per spec §2.2.1) -->
        <div class="hidden md:block p-4 pt-2">
          <IssueTable :issues="items" data-testid="issues-table" />
        </div>

        <!-- Mobile card list (≤768px) -->
        <div class="block md:hidden p-4 pt-2" data-testid="issues-mobile-list">
          <IssueCardList :issues="items" />
        </div>
      </template>

      <!-- Footer: showing count + load more -->
      <div class="flex items-center justify-between px-4 py-3 border-t border-border">
        <p class="text-caption text-muted font-mono tabular-nums">
          Showing <span class="text-fg">{{ items.length }}</span> of
          <span class="text-fg">{{ totalCount }}</span>
        </p>
        <div class="flex items-center gap-2">
          <BaseButton
            v-if="nextCursor"
            variant="secondary"
            size="sm"
            :loading="isLoadingMore"
            data-testid="load-more-button"
            @click="loadMore()"
          >
            Load more
          </BaseButton>
          <BaseButton variant="ghost" size="sm" @click="refresh()">
            Refresh
          </BaseButton>
        </div>
      </div>
    </BaseCard>
  </div>

  <!-- New issue modal -->
  <NewIssueModal
    v-if="showNewIssueModal"
    @close="showNewIssueModal = false"
    @created="onIssueCreated"
  />
</template>
